import { type JSX, PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { ActionButtonIntent, HeaderDivider, TriggerButton, TriggerButtonIntent } from "widgets";

import { type ISelectionModel } from "../../app-utils/selection-model.js";
import { pluralizeWord } from "../../app-utils/string.js";
import { type SampleType } from "../../graphql.js";
import { clearSampleTypeSelection, deleteSampleTypes } from "../../redux/modules/sample-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ModalActionWidget } from "../core/overlays/index.js";
import AddSampleTypeDialog from "./AddSampleTypeDialog.js";

interface ISampleTypesAdminToolbarOwnProps {
  renderOffScreen?: boolean; // Only useful for off-screen rendering
}

interface ISampleTypesAdminToolbarAppStateProps {
  // Redux injected props
  sampleTypes: SampleType[];
  selectionModel: ISelectionModel;
}

interface ISampleTypesAdminToolbarDispatchProps {
  deleteSampleType: (sampleTypeIds: number[]) => Promise<boolean>;
  clearSampleTypeSelection: () => void;
}

export interface ISampleTypesAdminToolbarProps
  extends ISampleTypesAdminToolbarAppStateProps,
    ISampleTypesAdminToolbarDispatchProps,
    ISampleTypesAdminToolbarOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: ISampleTypesAdminToolbarOwnProps,
): ISampleTypesAdminToolbarAppStateProps {
  return {
    sampleTypes: state.sampleAdmin.sampleTypes,
    selectionModel: state.sampleAdmin.sampleTypesSelectionModel,
  };
}

function mapDispatchToProps(
  dispatch,
  _ownProps: ISampleTypesAdminToolbarOwnProps,
): ISampleTypesAdminToolbarDispatchProps {
  return {
    deleteSampleType: (sampleTypeIds: number[]) => dispatch(deleteSampleTypes(sampleTypeIds)),
    clearSampleTypeSelection: () => dispatch(clearSampleTypeSelection()),
  };
}

interface ISampleTypesAdminToolbarState {
  showAddSampleType?: boolean;
}

const INIT_STATE: ISampleTypesAdminToolbarState = {
  showAddSampleType: false,
};

class SampleTypesAdminToolbarImpl extends PureComponent<ISampleTypesAdminToolbarProps, ISampleTypesAdminToolbarState> {
  constructor(props: ISampleTypesAdminToolbarProps) {
    super(props);
    this.state = INIT_STATE;
  }

  getDeleteButtonDisabledTooltip = (): string | JSX.Element => {
    if (this.props.selectionModel.selectedIds.length === 0) {
      return "Please make a selection to delete a sample type";
    } else {
      const selectedSampleTypes = this.props.selectionModel.selectedIds.map((selectedId: number) =>
        this.props.sampleTypes.find((sampleType: SampleType) => sampleType.id === selectedId),
      );
      const sampleTypesWithSamples = selectedSampleTypes.filter(
        (sampleType) => sampleType && (sampleType.sampleCount > 0 || sampleType.deletedSampleCount > 0),
      );
      if (sampleTypesWithSamples.length > 0) {
        return "A sample type can only be deleted if there are no samples associated with the sample type";
      }
      const sampleTypesAssociatedToPlaybooks = selectedSampleTypes.filter(
        (sampleType) => sampleType && sampleType.playbooks.length > 0,
      );
      if (sampleTypesAssociatedToPlaybooks.length > 0) {
        return (
          <span>
            A sample type can only be deleted if there are no playbooks associated with the sample type.
            {sampleTypesAssociatedToPlaybooks.map((sampleType, i) => (
              <span key={`sample-types-associated-to-playbooks-${i}`}>
                <br />
                Sample type "{sampleType.name}" is associated to playbook(s){" "}
                {sampleType.playbooks.map((playbook) => `"${playbook.name}"`).join(", ")}
              </span>
            ))}
          </span>
        );
      }
    }
  };

  handleDeleteSampleType = () => {
    return this.props.deleteSampleType(this.props.selectionModel.selectedIds as number[]).then(() => {
      this.props.clearSampleTypeSelection();
    });
  };

  render(): ReactElement {
    const deleteButtonDisabledTooltip = this.getDeleteButtonDisabledTooltip();

    return (
      <>
        <ModalActionWidget
          actionWidget={({ open, isOpen }) => (
            <TriggerButton
              id={"sample-type-admin-delete"}
              isActive={isOpen}
              tooltip={"Delete the selected sample type(s)"}
              onPress={() => open()}
              isDisabled={deleteButtonDisabledTooltip}
              intent={TriggerButtonIntent.Danger}
              icon={"delete"}
              // renderOffScreen={this.props.renderOffScreen}
              label={"Delete"}
              showLabel
            />
          )}
        >
          {({ close }) => (
            <ConfirmationDialog
              headerProps={{
                title: `Delete Sample ${pluralizeWord("Type", this.props.selectionModel.selectedIds.length)}`,
              }}
              mainActionButtonProps={{
                children: `Delete`,
                onPress: () => this.handleDeleteSampleType().then(() => close()),
                intent: ActionButtonIntent.Danger,
              }}
              closeFn={close}
            >
              Are you sure you want to delete {this.props.selectionModel.selectedIds.length} selected sample{" "}
              {pluralizeWord("type", this.props.selectionModel.selectedIds.length)}?
            </ConfirmationDialog>
          )}
        </ModalActionWidget>
        <HeaderDivider />
        <TriggerButton
          key={"sample-type-admin-add"}
          icon={"add"}
          tooltip={"Create a new sample type"}
          onPress={(_next) => this.setState({ showAddSampleType: true })}
          label={"New Sample Type"}
          showLabel
        />

        {this.state.showAddSampleType && this.props.renderOffScreen !== true && (
          <AddSampleTypeDialog
            title={"Add New Sample Type"}
            onApply={() => this.setState({ showAddSampleType: false })}
            onClose={() => this.setState({ showAddSampleType: false })}
          />
        )}
      </>
    );
  }
}

const SampleTypesAdminToolbar = connect<
  ISampleTypesAdminToolbarAppStateProps,
  ISampleTypesAdminToolbarDispatchProps,
  ISampleTypesAdminToolbarOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SampleTypesAdminToolbarImpl);

export { SampleTypesAdminToolbar };
export default SampleTypesAdminToolbar;
